// src/pages/FriendsPage.tsx
import React, { useCallback, useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { usersService } from "../services/usersService";
import { HandlersAcquaintanceResponse } from "../generated/models";
import {
  Box,
  Container,
  Typography,
  Paper,
  Avatar,
  IconButton,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  CircularProgress,
  Alert,
  Snackbar,
  Card,
  CardContent,
  CardActions,
  Tooltip,
  InputAdornment,
  Fab,
} from "@mui/material";
import {
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Badge as BadgeIcon,
  Add as AddIcon,
  Search as SearchIcon,
} from "@mui/icons-material";

const FriendsPage: React.FC = () => {
  const { user } = useAuth();
  const [friends, setFriends] = useState<HandlersAcquaintanceResponse[] | null>(
    null
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Add friend dialog
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [friendEmail, setFriendEmail] = useState("");
  const [friendReference, setFriendReference] = useState("");
  const [addMethod, setAddMethod] = useState<"email" | "reference">("email");

  // Delete friend dialog
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedFriend, setSelectedFriend] =
    useState<HandlersAcquaintanceResponse | null>(null);

  // Search functionality
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch all friends
  const fetchFriends = useCallback(async () => {
    if (!user?.public_id) return;

    setLoading(true);
    try {
      const data = await usersService.getUserFriends(user.public_id);
      if (data === null) {
        setFriends([]);
      } else {
        setFriends(data);
      }
      setError("");
    } catch (err: any) {
      console.error("Failed to fetch friends:", err);
      setError("Failed to load friends. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [user?.public_id]);

  // Fetch friends when component mounts
  React.useEffect(() => {
    if (!user?.public_id || friends !== null) return;

    fetchFriends();
  }, [fetchFriends, friends, user]);

  // Handle add friend dialog
  const handleOpenAddDialog = () => {
    setFriendEmail("");
    setFriendReference("");
    setAddMethod("email");
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
  };

  // Handle delete friend dialog
  const handleOpenDeleteDialog = (friend: HandlersAcquaintanceResponse) => {
    setSelectedFriend(friend);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedFriend(null);
  };

  // Add a friend
  const handleAddFriend = async () => {
    if (!user?.public_id) return;

    const value =
      addMethod === "email" ? friendEmail.trim() : friendReference.trim();
    if (!value) {
      setError(`Friend ${addMethod} is required`);
      return;
    }

    setIsSubmitting(true);
    try {
      // Create the appropriate request body based on the selected method
      const requestBody: { [key: string]: string } = {};
      if (addMethod === "email") {
        requestBody.email = value;
      } else {
        requestBody.friend_reference = value;
      }

      await usersService.addFriend(user.public_id!, requestBody);
      setSuccess("Friend added successfully");
      handleCloseAddDialog();

      await fetchFriends();
    } catch (err: any) {
      console.error("Failed to add friend:", err);
      setError(err.message || "Failed to add friend");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Remove a friend
  const handleRemoveFriend = async () => {
    if (!user?.public_id || !selectedFriend?.user?.public_id || !friends)
      return;

    setIsSubmitting(true);
    try {
      await usersService.removeFriend(
        user.public_id,
        selectedFriend.user?.public_id
      );
      setFriends(
        friends.filter(
          (f) => f.user?.public_id !== selectedFriend.user?.public_id
        )
      );
      setSuccess("Friend removed successfully");
      handleCloseDeleteDialog();
    } catch (err: any) {
      console.error("Failed to remove friend:", err);
      setError(err.message || "Failed to remove friend");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to get initials from name
  const getInitials = (name: string): string => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((part) => part.charAt(0))
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setError("");
    setSuccess("");
  };

  // Filter friends based on search query
  const filteredFriends = useMemo(() => {
    return friends?.filter((friend) => {
      const query = searchQuery.toLowerCase();
      return (
        friend.user?.name?.toLowerCase().includes(query) ||
        false ||
        friend.user?.email?.toLowerCase().includes(query) ||
        false
      );
    });
  }, [friends, searchQuery]);

  return (
    <Container sx={{ py: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          color="primary"
          sx={{ fontWeight: "bold" }}
        >
          My Friends
        </Typography>
        <Tooltip title="Add New Friend">
          <Fab
            color="primary"
            aria-label="add"
            onClick={handleOpenAddDialog}
            sx={{ boxShadow: 2 }}
          >
            <AddIcon />
          </Fab>
        </Tooltip>
      </Box>

      {/* Search bar */}
      <Paper
        sx={{
          p: 2,
          mb: 4,
          display: "flex",
          alignItems: "center",
          borderRadius: 2,
        }}
        elevation={1}
      >
        <TextField
          fullWidth
          placeholder="Search by name or email"
          variant="outlined"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="primary" />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress color="primary" />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : friends?.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
          <PersonAddIcon
            sx={{ fontSize: 60, color: "primary.main", opacity: 0.6, mb: 2 }}
          />
          <Typography variant="h6" gutterBottom>
            No Friends Found
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            You haven't added any friends yet. Add friends to make expense
            sharing easier.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<PersonAddIcon />}
            onClick={handleOpenAddDialog}
          >
            Add Your First Friend
          </Button>
        </Paper>
      ) : filteredFriends?.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
          <SearchIcon
            sx={{ fontSize: 60, color: "primary.main", opacity: 0.6, mb: 2 }}
          />
          <Typography variant="h6" gutterBottom>
            No Results Found
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            No friends match your search criteria.
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setSearchQuery("")}
          >
            Clear Search
          </Button>
        </Paper>
      ) : (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
          {filteredFriends?.map((friend) => (
            <Box
              key={friend.user?.public_id}
              sx={{
                flex: {
                  xs: "1 1 100%",
                  sm: "1 1 calc(50% - 12px)",
                  md: "1 1 calc(33.333% - 16px)",
                },
                maxWidth: {
                  xs: "100%",
                  sm: "calc(50% - 12px)",
                  md: "calc(33.333% - 16px)",
                },
              }}
            >
              <Card
                sx={{
                  height: "100%",
                  transition:
                    "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: 4,
                  },
                  borderRadius: 2,
                }}
              >
                <CardContent sx={{ pb: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Avatar
                      sx={{
                        width: 60,
                        height: 60,
                        bgcolor: "secondary.main",
                        mr: 2,
                      }}
                    >
                      {getInitials(friend.user?.name || "")}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" component="h2" noWrap>
                        {friend.user?.name}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <EmailIcon
                      fontSize="small"
                      color="primary"
                      sx={{ mr: 1 }}
                    />
                    <Typography variant="body2" noWrap>
                      {friend.user?.email || "No email provided"}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <BadgeIcon
                      fontSize="small"
                      color="primary"
                      sx={{ mr: 1 }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontSize: "0.75rem" }}
                      noWrap
                    >
                      Friend reference: {friend.user?.friend_reference}
                    </Typography>
                  </Box>
                </CardContent>

                <CardActions sx={{ justifyContent: "flex-end", pt: 0 }}>
                  <Tooltip title="Remove Friend">
                    <IconButton
                      onClick={() => handleOpenDeleteDialog(friend)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Card>
            </Box>
          ))}
        </Box>
      )}

      {/* Add Friend Dialog */}
      <Dialog open={openAddDialog} onClose={handleCloseAddDialog}>
        <DialogTitle sx={{ color: "primary.main" }}>Add a Friend</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Add a friend by email or friend reference code.
          </DialogContentText>

          <Box sx={{ mb: 2 }}>
            <Button
              variant={addMethod === "email" ? "contained" : "outlined"}
              color="primary"
              onClick={() => setAddMethod("email")}
              sx={{ mr: 1 }}
            >
              By Email
            </Button>
            <Button
              variant={addMethod === "reference" ? "contained" : "outlined"}
              color="primary"
              onClick={() => setAddMethod("reference")}
            >
              By Reference
            </Button>
          </Box>

          {addMethod === "email" ? (
            <TextField
              autoFocus
              margin="dense"
              id="email"
              label="Friend's Email"
              type="email"
              fullWidth
              variant="outlined"
              value={friendEmail}
              onChange={(e) => setFriendEmail(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="primary" />
                  </InputAdornment>
                ),
              }}
            />
          ) : (
            <TextField
              autoFocus
              margin="dense"
              id="reference"
              label="Friend Reference Code"
              type="text"
              fullWidth
              variant="outlined"
              value={friendReference}
              onChange={(e) => setFriendReference(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BadgeIcon color="primary" />
                  </InputAdornment>
                ),
              }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseAddDialog} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleAddFriend}
            variant="contained"
            color="primary"
            disabled={
              isSubmitting ||
              (addMethod === "email"
                ? !friendEmail.trim()
                : !friendReference.trim())
            }
            startIcon={<PersonAddIcon />}
          >
            {isSubmitting ? <CircularProgress size={24} /> : "Add Friend"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Friend Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle sx={{ color: "error.main" }}>Remove Friend</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove{" "}
            <strong>{selectedFriend?.user?.name}</strong> from your friends?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDeleteDialog} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleRemoveFriend}
            variant="contained"
            color="error"
            disabled={isSubmitting}
          >
            {isSubmitting ? <CircularProgress size={24} /> : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbars for feedback */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: "100%" }}
        >
          {success}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default FriendsPage;
